import { useEffect, useState, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { History, Calculator, TrendingUp, Save, Briefcase } from "lucide-react";
import { formatCompCurrency, budgetBadgeClass, budgetLabel } from "@/lib/budget";
import CompensationHistory from "@/components/candidate/CompensationHistory";

const CURRENCIES = ["INR", "USD", "EUR", "GBP", "AED", "SGD", "AUD", "CAD"];

const emptyForm = {
    current_salary: "",
    desired_salary_min: "",
    desired_salary_max: "",
    salary_currency: "INR",
    salary_period: "annual",
    notice_period_days: "",
    willing_to_relocate: false,
    expectation_notes: "",
    change_reason: "",
};

function snapshotToForm(s) {
    if (!s) return { ...emptyForm };
    return {
        current_salary: s.current_salary ?? "",
        desired_salary_min: s.desired_salary_min ?? "",
        desired_salary_max: s.desired_salary_max ?? "",
        salary_currency: s.salary_currency || "INR",
        salary_period: s.salary_period || "annual",
        notice_period_days: s.notice_period_days ?? "",
        willing_to_relocate: !!s.willing_to_relocate,
        expectation_notes: s.expectation_notes || "",
        change_reason: "",
    };
}

export default function CandidateCompensation({ candidateId }) {
    const [salary, setSalary] = useState(null);
    const [history, setHistory] = useState([]);
    const [scenarios, setScenarios] = useState([]);
    const [form, setForm] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    const [showHistory, setShowHistory] = useState(false);

    const load = async () => {
        try {
            const [s, h, sc] = await Promise.all([
                api.get(`/candidates/${candidateId}/salary`),
                api.get(`/candidates/${candidateId}/salary-history`),
                api.get(`/candidates/${candidateId}/compensation-scenarios`),
            ]);
            setSalary(s.data);
            setForm(snapshotToForm(s.data));
            setHistory(h.data?.items || []);
            setScenarios(sc.data?.items || []);
        } catch (err) {
            toast.error(formatApiError(err));
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [candidateId]);

    const save = async (e) => {
        e?.preventDefault?.();
        setBusy(true);
        try {
            const payload = { ...form };
            for (const k of ["current_salary", "desired_salary_min", "desired_salary_max", "notice_period_days"]) {
                const v = payload[k];
                payload[k] = (v === "" || v === null || v === undefined) ? null : Number(v);
            }
            await api.patch(`/candidates/${candidateId}/salary`, payload);
            toast.success("Compensation expectations saved");
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    // Calculator: current_salary × (1 + increment%)
    const [incPct, setIncPct] = useState(30);
    const [bonusPct, setBonusPct] = useState(10);
    const baseCurrent = Number(form.current_salary || 0);
    const projected = useMemo(() => {
        if (!baseCurrent) return { base: 0, hike: 0, bonus: 0, ctc: 0 };
        const hike = baseCurrent * (incPct / 100);
        const baseNew = baseCurrent + hike;
        const bonus = baseNew * (bonusPct / 100);
        const ctc = baseNew + bonus;
        return { base: baseNew, hike, bonus, ctc };
    }, [baseCurrent, incPct, bonusPct]);

    const fmt = (n) => formatCompCurrency(n, form.salary_currency, form.salary_period);

    return (
        <div className="space-y-8" data-testid="candidate-compensation">
            <form onSubmit={save} className="border border-border bg-white p-4 sm:p-6 space-y-6">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display font-bold text-lg">Compensation expectations</h3>
                        <p className="text-xs text-muted-foreground mt-1">
                            Saving creates a new version. Previous values are kept in history for reference.
                            {salary?.last_salary_updated_at && (
                                <span className="ml-1 font-mono">
                                    · Last updated {new Date(salary.last_salary_updated_at).toLocaleString()}
                                    {salary?.last_salary_updated_by_name ? ` by ${salary.last_salary_updated_by_name}` : ""}
                                </span>
                            )}
                        </p>
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Currency</Label>
                        <Select value={form.salary_currency} onValueChange={(v) => set("salary_currency", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="cand-salary-currency"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Period</Label>
                        <Select value={form.salary_period} onValueChange={(v) => set("salary_period", v)}>
                            <SelectTrigger className="rounded-none mt-2 h-11" data-testid="cand-salary-period"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="annual">Annual</SelectItem>
                                <SelectItem value="monthly">Monthly</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Notice period (days)</Label>
                        <Input
                            type="number" min={0}
                            value={form.notice_period_days ?? ""}
                            onChange={(e) => set("notice_period_days", e.target.value)}
                            placeholder="e.g. 30"
                            className="rounded-none mt-2 h-11"
                            data-testid="cand-notice-period"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Current salary</Label>
                        <Input
                            type="number" min={0} step={1000}
                            value={form.current_salary ?? ""}
                            onChange={(e) => set("current_salary", e.target.value)}
                            placeholder="Total CTC"
                            className="rounded-none mt-2 h-11"
                            data-testid="cand-current-salary"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Desired min</Label>
                        <Input
                            type="number" min={0} step={1000}
                            value={form.desired_salary_min ?? ""}
                            onChange={(e) => set("desired_salary_min", e.target.value)}
                            placeholder="Floor they'd accept"
                            className="rounded-none mt-2 h-11"
                            data-testid="cand-desired-min"
                        />
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-widest font-bold">Desired max</Label>
                        <Input
                            type="number" min={0} step={1000}
                            value={form.desired_salary_max ?? ""}
                            onChange={(e) => set("desired_salary_max", e.target.value)}
                            placeholder="Target ask"
                            className="rounded-none mt-2 h-11"
                            data-testid="cand-desired-max"
                        />
                    </div>
                </div>

                <div className="grid md:grid-cols-3 gap-4 items-end">
                    <label className="inline-flex items-center gap-2 text-sm md:col-span-1">
                        <input
                            type="checkbox"
                            checked={!!form.willing_to_relocate}
                            onChange={(e) => set("willing_to_relocate", e.target.checked)}
                            data-testid="cand-willing-relocate"
                        />
                        <span>Willing to relocate</span>
                    </label>
                    <div className="md:col-span-2">
                        <Label className="text-xs uppercase tracking-widest font-bold">Reason for this change</Label>
                        <Input
                            value={form.change_reason || ""}
                            onChange={(e) => set("change_reason", e.target.value)}
                            placeholder="e.g. updated after recruiter call · counter offer · revised market data"
                            className="rounded-none mt-2 h-11"
                            data-testid="cand-change-reason"
                        />
                    </div>
                </div>

                <div>
                    <Label className="text-xs uppercase tracking-widest font-bold">Notes / context</Label>
                    <Textarea
                        rows={3}
                        value={form.expectation_notes || ""}
                        onChange={(e) => set("expectation_notes", e.target.value)}
                        placeholder="Any nuance: equity preferences, joining bonus expectations, hard floor…"
                        className="rounded-none mt-2"
                        data-testid="cand-expectation-notes"
                    />
                </div>

                <div className="flex items-center gap-3">
                    <Button type="submit" disabled={busy} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="cand-comp-save">
                        <Save className="w-4 h-4 mr-2" /> {busy ? "Saving…" : "Save (creates version)"}
                    </Button>
                    {history.length > 0 && (
                        <Button type="button" variant="outline" onClick={() => setShowHistory((v) => !v)} className="rounded-none" data-testid="cand-comp-history-toggle">
                            <History className="w-4 h-4 mr-2" />
                            {showHistory ? "Hide" : "Show"} history ({history.length})
                        </Button>
                    )}
                </div>
            </form>

            {showHistory && history.length > 0 && (
                <section className="border border-border bg-white p-4 sm:p-6" data-testid="cand-comp-history">
                    <h3 className="font-display font-bold text-lg mb-4">Version history</h3>
                    <ol className="space-y-3 border-l-2 border-ikb/30 pl-4">
                        {history.map((v) => {
                            const s = v.snapshot || {};
                            return (
                                <li key={v.id} className={`relative ${v.is_current ? "" : "opacity-90"}`}>
                                    <span className={`absolute -left-[22px] top-1.5 w-3 h-3 rounded-full ${v.is_current ? "bg-ikb" : "bg-foreground/30"}`} />
                                    <div className="flex items-start justify-between gap-3 flex-wrap">
                                        <div>
                                            <div className="text-xs font-mono uppercase tracking-widest text-foreground/70">
                                                {v.is_current ? "Current" : "Previous"}
                                                {v.archived_at && <> · {new Date(v.archived_at).toLocaleString()}</>}
                                                {v.archived_by_name && <> · {v.archived_by_name}</>}
                                            </div>
                                            <div className="text-sm mt-1">
                                                <span className="font-mono text-foreground/80">Current</span> {formatCompCurrency(s.current_salary, s.salary_currency, s.salary_period)}
                                                {" · "}
                                                <span className="font-mono text-foreground/80">Desired</span> {formatCompCurrency(s.desired_salary_min, s.salary_currency, s.salary_period)} – {formatCompCurrency(s.desired_salary_max, s.salary_currency, s.salary_period)}
                                            </div>
                                            {v.reason && <div className="text-xs text-foreground/80 mt-1 italic">“{v.reason}”</div>}
                                            {s.expectation_notes && <div className="text-xs text-foreground/80 mt-1">{s.expectation_notes}</div>}
                                        </div>
                                    </div>
                                </li>
                            );
                        })}
                    </ol>
                </section>
            )}

            {/* Calculator */}
            <section className="border border-border bg-white p-4 sm:p-6 space-y-4" data-testid="cand-comp-calculator">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                        <h3 className="font-display font-bold text-lg flex items-center gap-2">
                            <Calculator className="w-5 h-5 text-ikb" /> Offer calculator
                        </h3>
                        <p className="text-xs text-muted-foreground mt-1">Play with hike % and bonus % to model a final CTC scenario.</p>
                    </div>
                </div>
                {baseCurrent === 0 ? (
                    <div className="text-sm text-muted-foreground italic">Add a current salary above to use the calculator.</div>
                ) : (
                    <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-4">
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs uppercase tracking-widest font-bold">Hike</Label>
                                    <span className="font-mono text-sm">{incPct}%</span>
                                </div>
                                <input
                                    type="range" min={0} max={100} step={1}
                                    value={incPct}
                                    onChange={(e) => setIncPct(Number(e.target.value))}
                                    className="w-full mt-2"
                                    data-testid="cand-calc-hike"
                                />
                            </div>
                            <div>
                                <div className="flex items-center justify-between">
                                    <Label className="text-xs uppercase tracking-widest font-bold">Bonus / variable</Label>
                                    <span className="font-mono text-sm">{bonusPct}%</span>
                                </div>
                                <input
                                    type="range" min={0} max={50} step={1}
                                    value={bonusPct}
                                    onChange={(e) => setBonusPct(Number(e.target.value))}
                                    className="w-full mt-2"
                                    data-testid="cand-calc-bonus"
                                />
                            </div>
                        </div>
                        <div className="space-y-2 border border-border bg-secondary/40 p-4">
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/80">Current</span>
                                <span className="font-mono">{fmt(baseCurrent)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/80">Hike (+{incPct}%)</span>
                                <span className="font-mono text-emerald-700">+{fmt(projected.hike)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/80">New base</span>
                                <span className="font-mono">{fmt(projected.base)}</span>
                            </div>
                            <div className="flex items-center justify-between text-sm">
                                <span className="text-foreground/80">Bonus ({bonusPct}%)</span>
                                <span className="font-mono">+{fmt(projected.bonus)}</span>
                            </div>
                            <div className="border-t border-border pt-2 mt-1 flex items-center justify-between">
                                <span className="font-display font-bold uppercase tracking-widest text-xs">Final CTC</span>
                                <span className="font-mono text-lg font-bold text-ikb" data-testid="cand-calc-final">{fmt(projected.ctc)}</span>
                            </div>
                            <div className="text-[11px] font-mono text-foreground/70 pt-1">
                                <TrendingUp className="w-3 h-3 inline mr-1" />
                                Net over current: <strong>{((projected.ctc / baseCurrent - 1) * 100).toFixed(1)}%</strong>
                            </div>
                        </div>
                    </div>
                )}
            </section>

            {/* Per-job scenarios */}
            {scenarios.length > 0 && (
                <section className="border border-border bg-white p-4 sm:p-6 space-y-3" data-testid="cand-comp-scenarios">
                    <h3 className="font-display font-bold text-lg flex items-center gap-2">
                        <Briefcase className="w-5 h-5 text-ikb" /> Budget vs. open applications
                    </h3>
                    <p className="text-xs text-muted-foreground">How this candidate's expectations stack against each job they're applied to.</p>
                    <div className="border border-border divide-y divide-border">
                        {scenarios.map((sc) => (
                            <div key={sc.application_id} className="grid grid-cols-12 gap-3 p-3 text-sm items-center">
                                <div className="col-span-12 md:col-span-4">
                                    <div className="font-medium">{sc.job_title || "Job"}</div>
                                    <div className="text-xs font-mono text-foreground/70">Stage: {sc.stage || "Applied"}</div>
                                </div>
                                <div className="col-span-6 md:col-span-3 text-xs font-mono">
                                    Band: {sc.salary_min || sc.salary_max
                                        ? `${formatCompCurrency(sc.salary_min, sc.salary_currency, sc.salary_period)} – ${formatCompCurrency(sc.salary_max, sc.salary_currency, sc.salary_period)}`
                                        : <span className="text-foreground/60">Not set</span>}
                                </div>
                                <div className="col-span-6 md:col-span-3 text-xs">
                                    {sc.budget ? (
                                        <span className={`inline-block px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest border ${budgetBadgeClass(sc.budget.status)}`}>
                                            {budgetLabel(sc.budget.status)}
                                            {sc.budget.delta_pct ? ` · ${sc.budget.delta_pct > 0 ? "+" : ""}${sc.budget.delta_pct}%` : ""}
                                        </span>
                                    ) : <span className="text-foreground/60">—</span>}
                                </div>
                                <div className="col-span-12 md:col-span-2 text-right text-xs font-mono">
                                    Match {sc.match_score || 0}/100
                                </div>
                            </div>
                        ))}
                    </div>
                </section>
            )}

            {/* Compensation history + AI benchmark + jump justifier */}
            <CompensationHistory candidateId={candidateId} />
        </div>
    );
}
